/* ==========================================================================
   로그인 후 돌아갈 경로

   보호된 페이지에 바로 들어오면 /login 으로 튕기는데, MS 로그인을 마치고
   /redirect 로 돌아왔을 때 원래 가려던 곳을 알 방법이 없어서 늘 루트로 갔습니다.

   OAuth 의 state 파라미터를 쓰는 게 정석이지만, 백엔드가 로그인 URL 을 만들 때
   state 를 '/' 로 고정하고 쿼리로 넘긴 값을 무시합니다. 그래서 프론트에서만
   해결합니다 — /login 에서 sessionStorage 에 담고 /redirect 에서 꺼내 씁니다.

   탭 안에서만 살아 있으면 되는 값이라 sessionStorage 가 맞습니다.
   MS 로그인은 같은 탭에서 이동했다 돌아오므로 값이 유지됩니다.
   ========================================================================== */

const KEY = 'loginReturnPath';

/**
 * 같은 출처의 내부 경로인지 검사합니다.
 *
 * 이 값은 URL 쿼리로 들어오므로 그대로 믿고 이동하면 오픈 리다이렉트가 됩니다.
 * `//evil.com` 이나 `/\evil.com` 은 브라우저가 외부 주소로 해석하므로 함께 막습니다.
 */
export const isSafeReturnPath = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  if (value.length === 0 || value.length > 512) return false;
  // 개행·제어문자가 섞인 값은 받지 않습니다.
  if (/[\u0000-\u001f\u007f]/.test(value)) return false;
  if (!value.startsWith('/')) return false;
  // 스킴 상대 URL(//host) 과 역슬래시 변형(/\host) 은 외부로 나갑니다.
  if (value.startsWith('//') || value.startsWith('/\\')) return false;
  return true;
};

/** /login 에서 호출 — 돌아갈 경로를 담아 둡니다. */
export const saveLoginReturnPath = (value: unknown) => {
  if (typeof window === 'undefined') return;
  if (!isSafeReturnPath(value)) return;
  try {
    window.sessionStorage.setItem(KEY, value);
  } catch (e) {
    // 시크릿 모드 등에서 sessionStorage 가 막혀 있어도 로그인 자체는 되어야 합니다.
  }
};

/**
 * /redirect 에서 호출 — 담아 둔 경로를 꺼내고 지웁니다.
 * 한 번 쓰고 버려야 다음 로그인 때 엉뚱한 곳으로 가지 않습니다.
 */
export const takeLoginReturnPath = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
    return isSafeReturnPath(value) ? value : null;
  } catch (e) {
    return null;
  }
};

/** 보호된 페이지의 getServerSideProps 에서 쓸 /login 목적지를 만듭니다. */
export const loginDestination = (returnPath: string) =>
  isSafeReturnPath(returnPath)
    ? `/login?next=${encodeURIComponent(returnPath)}`
    : '/login';
